import ApiService from "@/core/services/api.service";

// action types
export const GET_DIRECTORY_CONTENT = "getDirectoryContent";
export const GET_DIRECTORY_SIZE = "getDirectorySize";
export const REFRESH_DIRECTORY = "refreshDirectory";
export const HISTORY_BACK = "historyBack";
export const HISTORY_FORWARD = "historyForward";
export const SORT_BY = "sortBy";
export const GET_FILE = "getFile";
export const CREATE_DIRECTORY = "createDirectory";
export const UPLOAD = "upload";
export const DELETE = "delete";
export const REFRESH_ALL = "refreshAll";
export const REPEAT_SORT = "repeatSort";
export const RESET_STATE = "resetState";
export const TO_CLIPBOARD = "toClipboard";
export const PASTE = "paste";

// mutation types
export const SET_SM_USER = "setStorageManagerUser";
export const SET_DIRECTORY_CONTENT = "setDirectoryContent";
export const SET_FILE = "setFile";
export const SET_DIRECTORY_SIZE = "setDirectorySize";
export const SET_CURRENT_DIRECTORY = "setCurrentDirectory";
export const SET_SELECTED = "setSelected";
export const REMOVE_SELECTED = "removeSelected";
export const CHANGE_SELECTED = "changeSelected";
export const RESET_SELECTED = "resetSelected";
export const POINTER_BACK = "pointerBack";
export const POINTER_FORWARD = "pointerForward";
export const ADD_TO_HISTORY = "addToHistory";
export const RESET_HISTORY = "resetHistory";
export const SET_SORT_FIELD = "setSortField";
export const SET_SORT_DIRECTION = "setSortDirection";
export const RESET_SORT_SETTING = "resetSortSettings";
export const SORT_BY_NAME = "sortByName";
export const SORT_BY_SIZE = "sortBySize";
export const SORT_BY_TYPE = "sortByType";
export const SORT_BY_DATE = "sortByDate";
export const START_LOADING = "startLoading";
export const STOP_LOADING = "stopLoading";
export const SET_PROGRESS = "setProgress";
export const CLEAR_PROGRESS = "clearProgress";
export const SCREEN_TOGGLE = "screenToggle";
export const SET_CLIPBOARD = "setClipboard";
export const RESET_CLIPBOARD = "resetClipboard";

const state = {
  smUser: "",
  currentDirectory: "/",
  directorySize: 0,
  directories: [],
  files: [],
  file: [],
  selected: {
    directories: [],
    files: []
  },
  sort: {
    field: "name",
    direction: "up"
  },
  history: [null],
  historyPointer: 0,
  fileManagerLoading: false,
  fileManagerProgress: 0,
  clipboard: {
    type: null,
    directories: [],
    files: []
  },
  fullScreen: false
};

const getters = {
  fileManagerLoading(state) {
    return state.fileManagerLoading;
  },
  fileManagerProgress(state) {
    return state.fileManagerProgress;
  },
  currentDirectory(state) {
    return state.currentDirectory;
  },
  directorySize(state) {
    return state.directorySize;
  },
  sort(state) {
    return state.sort;
  },
  filesCount(state, getters) {
    return getters.files.length;
  },
  directoriesCount(state, getters) {
    return getters.directories.length;
  },
  filesSize(state, getters) {
    if (getters.files.length) {
      return getters.files.reduce(
        (previous, current) => previous + Number(current.size),
        0
      );
    }

    return 0;
  },
  selectedCount(state, getters) {
    return getters.selectedList.length;
  },
  selectedFilesSize(state) {
    const selectedFiles = state.files.filter(file =>
      state.selected.files.includes(file.path)
    );

    if (selectedFiles.length) {
      return selectedFiles.reduce(
        (previous, current) => previous + Number(current.size),
        0
      );
    }

    return 0;
  },
  selectedList(state) {
    const selectedDirectories = state.directories.filter(directory =>
      state.selected.directories.includes(directory.path)
    );

    const selectedFiles = state.files.filter(file =>
      state.selected.files.includes(file.path)
    );

    return selectedDirectories.concat(selectedFiles);
  },
  breadcrumb(state) {
    if (state.currentDirectory && state.currentDirectory !== "/") {
      return state.currentDirectory.replace(/^\/+|\/+$/, "").split("/");
    }

    return null;
  },
  directoryExist: state => basename =>
    state.directories.some(el => el.basename === basename),
  fileExist: state => basename =>
    state.files.some(el => el.basename === basename)
};

const actions = {
  [SET_SM_USER](context, id = null) {
    if (id) {
      context.commit(SET_SM_USER, id);
    } else {
      context.commit(SET_SM_USER, context.getters.currentUserPersonalInfo.id);
    }
  },
  [GET_DIRECTORY_SIZE](context, payload) {
    if (payload && payload.user) {
      context.commit(SET_SM_USER, payload.user);
    } else {
      context.commit(SET_SM_USER, context.getters.currentUserPersonalInfo.id);
    }

    return new Promise((resolve, reject) => {
      ApiService.query(
        "storage/directories/size",
        {
          params: {
            path: payload ? payload.path : "/",
            user: context.state.smUser
          }
        },
        false
      )
        .then(({ data }) => {
          resolve(data);
          context.commit(SET_DIRECTORY_SIZE, data.size);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },

  [GET_DIRECTORY_CONTENT](context, { path, history }) {
    context.commit(SET_DIRECTORY_CONTENT, { directories: [], files: [] });

    return new Promise((resolve, reject) => {
      context.commit(START_LOADING);
      ApiService.query("storage/directories", {
        params: {
          path: path,
          user: context.state.smUser
        }
      })
        .then(({ data }) => {
          context.commit(STOP_LOADING);
          context.commit(RESET_SELECTED);
          context.commit(RESET_SORT_SETTING);
          context.commit(SET_DIRECTORY_CONTENT, data);
          context.commit(SET_CURRENT_DIRECTORY, path);

          if (history) context.commit(ADD_TO_HISTORY, path);
          resolve(data);
        })
        .catch(({ response }) => {
          context.commit(STOP_LOADING);
          reject(response);
        });
    });
  },
  [REFRESH_DIRECTORY]({ state, commit, dispatch }) {
    return new Promise(resolve => {
      commit(START_LOADING);
      commit(RESET_SELECTED);
      commit(RESET_SORT_SETTING);
      commit(RESET_HISTORY);

      ApiService.query("storage/directories", {
        params: {
          path: state.currentDirectory,
          user: state.smUser
        }
      })
        .then(({ data }) => {
          commit(STOP_LOADING);
          if (state.currentDirectory) {
            commit(ADD_TO_HISTORY, state.currentDirectory);
          }
          commit(SET_DIRECTORY_CONTENT, data);

          resolve(data);
        })
        .catch(() => {
          commit(STOP_LOADING);
          commit(SET_CURRENT_DIRECTORY, "/");
          dispatch(REFRESH_DIRECTORY);
        });
    });
  },

  [HISTORY_BACK]({ state, commit, dispatch }) {
    dispatch(GET_DIRECTORY_CONTENT, {
      path: state.history[state.historyPointer - 1] ?? "/",
      history: false
    });
    commit(POINTER_BACK);
  },

  [HISTORY_FORWARD]({ state, commit, dispatch }) {
    dispatch(GET_DIRECTORY_CONTENT, {
      path: state.history[state.historyPointer + 1] ?? "/",
      history: false
    });
    commit(POINTER_FORWARD);
  },

  [SORT_BY]({ state, commit }, { field, direction }) {
    if (state.sort.field === field && !direction) {
      commit(SET_SORT_DIRECTION, state.sort.direction === "up" ? "down" : "up");
    } else if (direction) {
      commit(SET_SORT_DIRECTION, direction);
      commit("setSortField", field);
    } else {
      commit(SET_SORT_DIRECTION, "up");
      commit("setSortField", field);
    }
    // sort by field type
    switch (field) {
      case "name":
        commit(SORT_BY_NAME);
        break;
      case "size":
        commit(SORT_BY_SIZE);
        break;
      case "type":
        commit(SORT_BY_TYPE);
        break;
      case "date":
        commit(SORT_BY_DATE);
        break;
      default:
        break;
    }
  },

  [GET_FILE](context, { path }) {
    return new Promise((resolve, reject) => {
      ApiService.query("storage/files", {
        params: {
          path: path,
          user: context.state.smUser
        }
      })
        .then(({ data }) => {
          context.commit(SET_FILE, data);
          resolve(data.url);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [CREATE_DIRECTORY]({ state, getters, commit, dispatch }, name) {
    const currentDirectory = getters.currentDirectory;

    return new Promise((resolve, reject) => {
      commit(START_LOADING);
      ApiService.post("storage/directories", {
        path: currentDirectory + "/" + name,
        user: state.smUser
      })
        .then(({ data }) => {
          commit(STOP_LOADING);
          dispatch(REFRESH_ALL);
          resolve(data);
        })
        .catch(({ response }) => {
          commit(STOP_LOADING);
          reject(response);
        });
    });
  },

  [UPLOAD]({ getters, commit, dispatch }, files) {
    const currentDirectory = getters.currentDirectory;

    const data = new FormData();
    data.append("path", currentDirectory || "");
    data.append("user", state.smUser);

    // for (let i = 0; i < files.length; i += 1) {
    //   data.append("files[]", files[i]);
    // }
    data.append("file", files[0]);

    // axios config - progress bar
    const config = {
      onUploadProgress(progressEvent) {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        commit(SET_PROGRESS, progress);
      }
    };

    return new Promise(resolve => {
      commit(START_LOADING);
      ApiService.post("storage/files", data, config)
        .then(({ data }) => {
          commit(CLEAR_PROGRESS);
          commit(STOP_LOADING);
          dispatch(REFRESH_DIRECTORY);

          resolve(data);
        })
        .catch(() => {
          commit(STOP_LOADING);
          commit(CLEAR_PROGRESS);
        });
    });
  },
  [DELETE](context, items) {
    const deleteItem = item => {
      context.commit(START_LOADING);

      return new Promise(async (resolve, reject) => {
        if (item.type === "dir") {
          ApiService.delete("storage/directories", {
            data: {
              path: item.path,
              user: context.state.smUser
            }
          })
            .then(response => {
              context.commit(STOP_LOADING);
              resolve(response);
            })
            .catch(() => {
              reject();
            });
        } else {
          ApiService.delete("storage/files", {
            data: {
              path: item.path,
              user: context.state.smUser
            }
          })
            .then(response => {
              context.commit(STOP_LOADING);
              resolve(response);
            })
            .catch(() => {
              reject();
            });
        }
      });
    };

    const deleteItems = async items => {
      await Promise.all(items.map(async item => await deleteItem(item)));
    };

    deleteItems(items)
      .then(() => {
        context.dispatch(REFRESH_ALL);
      })
      .catch(() => {
        context.commit(STOP_LOADING);
      });
  },

  [REFRESH_ALL]({ dispatch }) {
    return dispatch(REFRESH_DIRECTORY);
  },

  [REPEAT_SORT]({ state, dispatch }) {
    dispatch(SORT_BY, {
      field: state.sort.field,
      direction: state.sort.direction
    });
  },

  [RESET_STATE]({ commit }) {
    commit(SET_SM_USER, null);
    commit(SET_CURRENT_DIRECTORY, null);
    commit(SET_DIRECTORY_CONTENT, { directories: [], files: [] });
    commit(RESET_SELECTED);
    commit(RESET_SELECTED);
    commit(RESET_HISTORY);
  },

  [TO_CLIPBOARD]({ state, commit, getters }, type) {
    if (getters.selectedCount) {
      commit(SET_CLIPBOARD, {
        type,
        directories: state.selected.directories.slice(0),
        files: state.selected.files.slice(0)
      });
    }
  },

  [PASTE]({ state, commit, getters, dispatch }) {
    commit(START_LOADING);

    const pasteDirectory = directory => {
      let type = state.clipboard.type === "copy" ? 1 : 2;
      let current = getters.currentDirectory;

      let basename = path => {
        return path.split("/").reverse()[0];
      };

      let toPath = "";

      if (current !== "/") {
        toPath = current + "/" + basename(directory);
      } else {
        toPath = "/" + basename(directory);
      }

      return new Promise(async (resolve, reject) => {
        ApiService.update("storage/directories", "", {
          type: type,
          from: directory,
          to: toPath,
          user: state.smUser
        })
          .then(({ data }) => {
            resolve(data);
          })
          .catch(() => {
            reject();
          });
      });
    };
    const pasteFile = file => {
      let current = getters.currentDirectory;
      let type = state.clipboard.type === "copy" ? 1 : 2;

      let basename = path => {
        return path.split("/").reverse()[0];
      };

      let toPath = "";

      if (current !== "/") {
        toPath = current + "/" + basename(file);
      } else {
        toPath = "/" + basename(file);
      }

      return new Promise(async (resolve, reject) => {
        ApiService.update("storage/files", "", {
          type: type,
          from: file,
          to: toPath,
          user: state.smUser
        })
          .then(({ data }) => {
            resolve(data);
          })
          .catch(() => {
            reject();
          });
      });
    };

    const pasteAll = async () => {
      let directories = state.clipboard.directories;
      let files = state.clipboard.files;

      await Promise.all(
        directories.map(async directory => await pasteDirectory(directory))
      );
      await Promise.all(files.map(async file => await pasteFile(file)));
    };

    pasteAll(state.clipboard)
      .then(() => {
        dispatch(REFRESH_ALL);
        commit(STOP_LOADING);

        if (state.clipboard.type === "cut") {
          commit(RESET_CLIPBOARD);
        }
      })
      .catch(() => {
        dispatch(REFRESH_ALL);
        commit(RESET_CLIPBOARD);
        commit(STOP_LOADING);
      });
  }
};

const mutations = {
  [SET_SM_USER](state, id) {
    state.smUser = id;
  },
  /**
   * @param state
   * @param data
   */
  [SET_DIRECTORY_CONTENT](state, data) {
    let files = [],
      directories = [];

    data.files.forEach(item => {
      let basename = item.path.replace(/^.*[\\/]/, "");
      files.push({
        type: item.type,
        path: item.path,
        size: item.file_size,
        timestamp: item.last_modified,
        basename: basename,
        dirname: item.path.replace(basename, ""),
        extension: item.path.split(".").pop()
      });
    });

    data.directories.forEach(item => {
      let basename = item.replace(/^.*[\\/]/, "");
      directories.push({
        type: "dir",
        path: item,
        size: "",
        timestamp: "",
        basename: basename,
        dirname: item.replace(basename, "")
      });
    });
    state.directories = directories;
    state.files = files;
  },
  /**
   * @param state
   * @param data
   */
  [SET_FILE](state, data) {
    state.file = data;
  },
  /**
   * @param state
   * @param directory
   */
  [SET_CURRENT_DIRECTORY](state, directory) {
    state.currentDirectory = directory;
  },

  /**
   * @param state
   * @param size
   */
  [SET_DIRECTORY_SIZE](state, size) {
    state.directorySize = size;
  },

  /**
   * Set selected items
   * @param state
   * @param type (directories, files)
   * @param path
   */
  [SET_SELECTED](state, { type, path }) {
    state.selected[type].push(path);
  },

  /**
   * Remove item from array
   * @param state
   * @param arrayIndex
   */
  [REMOVE_SELECTED](state, { type, path }) {
    const itemIndex = state.selected[type].indexOf(path);
    if (itemIndex !== -1) state.selected[type].splice(itemIndex, 1);
  },

  /**
   * Change selected item
   * @param state
   * @param type
   * @param path
   */
  [CHANGE_SELECTED](state, { type, path }) {
    state.selected.directories = [];
    state.selected.files = [];
    state.selected[type].push(path);
  },

  /**
   * Reset selected items array
   * @param state
   */
  [RESET_SELECTED](state) {
    state.selected.directories = [];
    state.selected.files = [];
  },

  /**
   * Add new file
   * @param state
   * @param newFile
   */
  addNewFile(state, newFile) {
    state.files.push(newFile);
  },

  /**
   * Update file
   * @param state
   * @param file
   */
  updateFile(state, file) {
    const itemIndex = state.files.findIndex(
      el => el.basename === file.basename
    );
    if (itemIndex !== -1) state.files[itemIndex] = file;
  },

  /**
   * Add new directory
   * @param state
   * @param newDirectory
   */
  addNewDirectory(state, newDirectory) {
    state.directories.push(newDirectory);
  },

  /**
   * Change history pointer (back)
   * @param state
   */
  [POINTER_BACK](state) {
    state.historyPointer -= 1;
  },

  /**
   * Change history pointer (forward)
   * @param state
   */
  [POINTER_FORWARD](state) {
    state.historyPointer += 1;
  },

  /**
   * @param state
   * @param path
   */
  [ADD_TO_HISTORY](state, path) {
    if (state.historyPointer < state.history.length - 1) {
      // erase next elements in the history
      state.history.splice(state.historyPointer + 1, Number.MAX_VALUE);
    }
    // add new path
    state.history.push(path);
    // change history pointer
    state.historyPointer += 1;
  },

  /**
   * @param state
   */
  [RESET_HISTORY](state) {
    state.history = [null];
    state.historyPointer = 0;
  },

  /**
   * @param state
   * @param field
   */
  [SET_SORT_FIELD](state, field) {
    state.sort.field = field;
  },

  /**
   * @param state
   * @param direction
   */
  [SET_SORT_DIRECTION](state, direction) {
    state.sort.direction = direction;
  },

  /**
   * @param state
   */
  [RESET_SORT_SETTING](state) {
    state.sort.field = "name";
    state.sort.direction = "up";
  },

  /**
   * @param state
   */
  [SORT_BY_NAME](state) {
    if (state.sort.direction === "up") {
      state.directories.sort((a, b) => a.basename.localeCompare(b.basename));
      state.files.sort((a, b) => a.basename.localeCompare(b.basename));
    } else {
      state.directories.sort((a, b) => b.basename.localeCompare(a.basename));
      state.files.sort((a, b) => b.basename.localeCompare(a.basename));
    }
  },

  /**
   * @param state
   */
  [SORT_BY_SIZE](state) {
    state.directories.sort((a, b) => a.basename.localeCompare(b.basename));

    if (state.sort.direction === "up") {
      state.files.sort((a, b) => a.size - b.size);
    } else {
      state.files.sort((a, b) => b.size - a.size);
    }
  },

  /**
   * @param state
   */
  [SORT_BY_TYPE](state) {
    state.directories.sort((a, b) => a.basename.localeCompare(b.basename));

    if (state.sort.direction === "up") {
      state.files.sort((a, b) => a.extension.localeCompare(b.extension));
    } else {
      state.files.sort((a, b) => b.extension.localeCompare(a.extension));
    }
  },

  /**
   * @param state
   */
  [SORT_BY_DATE](state) {
    if (state.sort.direction === "up") {
      state.directories.sort((a, b) => a.timestamp - b.timestamp);
      state.files.sort((a, b) => a.timestamp - b.timestamp);
    } else {
      state.directories.sort((a, b) => b.timestamp - a.timestamp);
      state.files.sort((a, b) => b.timestamp - a.timestamp);
    }
  },

  [START_LOADING](state) {
    state.fileManagerLoading = true;
  },

  [STOP_LOADING](state) {
    state.fileManagerLoading = false;
  },

  [SET_PROGRESS](state, val) {
    state.fileManagerProgress = val;
    if (val >= 100) {
      state.fileManagerProgress = 0;
    }
  },

  [CLEAR_PROGRESS](state) {
    state.fileManagerProgress = 0;
  },

  [SCREEN_TOGGLE](state) {
    state.fullScreen = !state.fullScreen;
  },

  [SET_CLIPBOARD](state, { type, directories, files }) {
    state.clipboard.type = type;
    state.clipboard.directories = directories;
    state.clipboard.files = files;
  },

  [RESET_CLIPBOARD](state) {
    state.clipboard.type = null;
    state.clipboard.disk = null;
    state.clipboard.directories = [];
    state.clipboard.files = [];
  }
};

export default {
  state,
  actions,
  mutations,
  getters
};
